import { motion } from "framer-motion";
import { Shield, Globe, Landmark, ArrowRight, CheckCircle, Clock } from "lucide-react";
import { Link } from "react-router-dom";

const programs = [
  {
    country: "UAE",
    flag: "🇦🇪",
    programName: "Golden Visa",
    investmentMin: "AED 2M+",
    benefits: ["10-year renewable residency", "0% income & capital gains tax", "Family sponsorship included", "100% business ownership"],
    timeline: "2-4 weeks",
    highlight: "Tax-free living",
  },
  {
    country: "Portugal",
    flag: "🇵🇹",
    programName: "Golden Visa",
    investmentMin: "€500K+",
    benefits: ["Path to EU citizenship in 5 years", "Schengen zone access", "Family reunification", "No minimum stay requirement"],
    timeline: "6-8 months",
    highlight: "EU passport pathway",
  },
  {
    country: "Spain",
    flag: "🇪🇸",
    programName: "Golden Visa",
    investmentMin: "€500K+",
    benefits: ["Residency for investor & family", "Free movement across EU", "Access to world-class healthcare", "Path to permanent residency"],
    timeline: "2-3 months",
    highlight: "Mediterranean lifestyle",
  },
  {
    country: "Monaco",
    flag: "🇲🇨",
    programName: "Residency Permit",
    investmentMin: "€500K+ deposit",
    benefits: ["0% income tax for residents", "World's safest jurisdiction", "Elite social & business network", "No minimum stay required"],
    timeline: "3-6 months",
    highlight: "Ultimate tax efficiency",
  },
  {
    country: "Switzerland",
    flag: "🇨🇭",
    programName: "Lump-Sum Taxation",
    investmentMin: "CHF 400K+ annual",
    benefits: ["Favorable flat-rate taxation", "Political neutrality & stability", "World-class private banking", "Premier education system"],
    timeline: "3-6 months",
    highlight: "Wealth preservation",
  },
  {
    country: "UK",
    flag: "🇬🇧",
    programName: "Innovator Founder",
    investmentMin: "£50K+ (endorsed)",
    benefits: ["Access to London financial hub", "Path to settlement in 3 years", "World-renowned legal system", "Global business connectivity"],
    timeline: "3-8 weeks",
    highlight: "Financial capital access",
  },
];

const ResidencyVisaSection = () => {
  return (
    <section className="py-24 lg:py-32 bg-background overflow-hidden">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 lg:mb-20"
        >
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-primary" />
            <p className="font-body text-sm uppercase tracking-[0.3em] text-primary">
              Residency & Visa Programs
            </p>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground max-w-3xl leading-tight">
            Your Property,
            <span className="block italic font-medium text-primary">Your Passport</span>
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mt-6 leading-relaxed">
            Strategic property investment unlocks residency pathways in the world's most 
            desirable jurisdictions. We guide you through every step — from property selection 
            to visa acquisition.
          </p>
        </motion.div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {programs.map((program, index) => (
            <motion.div
              key={program.country}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              className="group relative bg-card border border-border/50 rounded-2xl p-6 transition-all duration-500 hover:border-primary/30 hover:shadow-luxury"
            >
              {/* Highlight tag */}
              <div className="absolute top-4 right-4">
                <span className="text-[10px] uppercase tracking-wider bg-primary/10 text-primary px-2.5 py-1 rounded-full font-medium">
                  {program.highlight}
                </span>
              </div>

              {/* Country header */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">{program.flag}</span>
                <div>
                  <h3 className="font-display text-xl text-foreground">{program.country}</h3>
                  <p className="text-xs text-muted-foreground">{program.programName}</p>
                </div>
              </div>

              {/* Investment minimum */}
              <div className="bg-secondary/50 rounded-xl p-3 mb-5">
                <p className="text-xs text-muted-foreground mb-1">Minimum Investment</p>
                <p className="font-display text-lg text-primary font-semibold">{program.investmentMin}</p>
              </div>

              {/* Benefits */}
              <ul className="space-y-2.5 mb-5">
                {program.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Timeline */}
              <div className="flex items-center gap-2 pt-4 border-t border-border/30">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Processing: {program.timeline}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-8 bg-secondary/50 border border-border/30 rounded-2xl px-8 py-6">
            <div className="flex items-center gap-3">
              <Landmark className="w-6 h-6 text-primary" />
              <div className="text-left">
                <p className="font-display text-sm text-foreground font-medium">Need Personalized Guidance?</p>
                <p className="text-xs text-muted-foreground">Our advisors specialize in cross-border residency strategies</p>
              </div>
            </div>
            <Link
              to="/#contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-body text-sm font-medium hover:opacity-90 transition-opacity"
            >
              Schedule Consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ResidencyVisaSection;
